import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, MessageCircle, Loader2, Trash2, CornerDownRight, Flag, Crown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PlayerModal from "@/components/PlayerModal";

const EMOJIS = ["👍", "👎", "❤️", "🔥", "🐴", "🍀", "😮"];

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function computeMentions(text, members) {
  const ids = [];
  for (const m of members) {
    if (m.name && text.includes("@" + m.name)) ids.push(m.id);
  }
  return ids;
}

function renderMentions(text, members, onMention) {
  const byName = {};
  for (const m of members) if (m.name) byName[m.name] = m.id;
  const list = Object.keys(byName).sort((a, b) => b.length - a.length);
  if (!list.length) return text;
  const re = new RegExp("@(" + list.map(escapeRegExp).join("|") + ")", "g");
  const out = [];
  let last = 0, m, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const id = byName[m[1]];
    out.push(
      <button
        key={i++}
        type="button"
        onClick={() => onMention && onMention(id)}
        data-testid={`mention-link-${id}`}
        className="text-[#39FF14] font-semibold hover:underline"
      >
        {m[0]}
      </button>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function MentionBox({ value, onChange, members, placeholder, onSubmit, testid, rows = 2 }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const suggestions = open
    ? members.filter((m) => m.name.toLowerCase().includes(query.toLowerCase())).slice(0, 6)
    : [];

  const handle = (e) => {
    const v = e.target.value;
    onChange(v);
    const caret = e.target.selectionStart || v.length;
    const before = v.slice(0, caret);
    const match = before.match(/@([^\s@]*)$/);
    if (match) { setQuery(match[1]); setOpen(true); } else { setOpen(false); }
  };

  const pick = (m) => {
    const el = ref.current;
    const caret = el ? el.selectionStart : value.length;
    const before = value.slice(0, caret).replace(/@([^\s@]*)$/, "@" + m.name + " ");
    const after = value.slice(caret);
    onChange(before + after);
    setOpen(false);
    setTimeout(() => { if (el) { el.focus(); const pos = before.length; el.setSelectionRange(pos, pos); } }, 0);
  };

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={value}
        onChange={handle}
        rows={rows}
        data-testid={testid}
        placeholder={placeholder}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !open) { e.preventDefault(); onSubmit && onSubmit(); }
          if (e.key === "Escape") setOpen(false);
        }}
        className="w-full bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#39FF14]"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-[#0B120E] border border-[#00E676]/25 rounded-lg overflow-hidden shadow-xl" data-testid="mention-suggestions">
          {suggestions.map((m) => (
            <button key={m.id} type="button" onClick={() => pick(m)} data-testid={`mention-opt-${m.id}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#1A2B20] flex items-center gap-2">
              <span className="text-[#39FF14]">@</span>{m.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ChampionCrown() {
  return (
    <span
      data-testid="feed-champion-crown"
      title="Champion de la semaine"
      className="inline-flex items-center shrink-0 text-[#FFD700]"
    >
      <Crown size={13} className="fill-[#FFD700]" />
    </span>
  );
}

function Avatar({ name, avatar }) {
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("");
  return avatar ? (
    <img src={avatar} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
  ) : (
    <span className="h-8 w-8 rounded-full grid place-items-center bg-[#1A2B20] text-[#39FF14] text-xs font-bold shrink-0">{initials}</span>
  );
}

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return new Date(iso).toLocaleDateString("fr-FR");
}

function Post({ post, me, onChange, members, onMention }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [showReplies, setShowReplies] = useState(false);

  const react = async (emoji) => {
    try {
      await api.post(`/feed/${post.id}/react`, { emoji });
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/feed/${post.id}/reply`, { text: reply, mentions: computeMentions(reply, members) });
      setReply(""); setReplyOpen(false); setShowReplies(true); onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async () => {
    try { await api.delete(`/feed/${post.id}`); onChange(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const report = async () => {
    const reason = window.prompt("Signaler ce message aux modérateurs — précisez la raison (optionnel) :", "");
    if (reason === null) return;
    try { await api.post(`/feed/${post.id}/report`, { reason }); toast.success("Message signalé aux modérateurs"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-[#00E676]/10 bg-[#0F1812] p-3" data-testid={`post-${post.id}`}>
      <div className="flex items-start gap-2">
        <Avatar name={post.author_name} avatar={post.author_avatar} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onMention && post.user_id && onMention(post.user_id)}
              data-testid={`post-author-${post.id}`}
              className="font-semibold text-sm truncate hover:text-[#39FF14] hover:underline"
            >
              {post.author_name}
            </button>
            {post.is_champion && <ChampionCrown />}
            <span className="text-[10px] text-[#697C6F]">{timeAgo(post.created_at)}</span>
            {(post.user_id === me?.id || me?.role === "admin" || me?.is_moderator) && (
              <button onClick={del} className="ml-auto text-[#697C6F] hover:text-[#FF3B30]"><Trash2 size={13} /></button>
            )}
          </div>
          <p className="text-sm text-[#DDE7E0] mt-0.5 whitespace-pre-wrap break-words">{renderMentions(post.text, members, onMention)}</p>

          <div className="flex items-center gap-1 mt-2 flex-wrap">
            {EMOJIS.map((e) => {
              const r = post.reactions?.find((x) => x.emoji === e);
              return (
                <button key={e} onClick={() => react(e)} data-testid={`react-${post.id}-${e}`}
                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${r?.mine ? "border-[#39FF14] bg-[#00E676]/15" : "border-[#00E676]/15 hover:bg-[#1A2B20]"}`}>
                  {e}{r?.count ? <span className="ml-1 tabular text-[#A1B5A8]">{r.count}</span> : ""}
                </button>
              );
            })}
            <button onClick={() => setReplyOpen(!replyOpen)} data-testid={`reply-btn-${post.id}`} className="text-xs px-2 py-1 rounded-full text-[#A1B5A8] hover:text-white flex items-center gap-1">
              <CornerDownRight size={12} /> Répondre
            </button>
            {post.user_id !== me?.id && (
              <button onClick={report} data-testid={`report-btn-${post.id}`} className="text-xs px-2 py-1 rounded-full text-[#A1B5A8] hover:text-[#FFCC00] flex items-center gap-1">
                <Flag size={12} /> Signaler
              </button>
            )}
          </div>

          {post.replies?.length > 0 && (
            <button onClick={() => setShowReplies(!showReplies)} className="text-[11px] text-[#39FF14] mt-2">
              {showReplies ? "Masquer" : `Voir ${post.replies.length} réponse${post.replies.length > 1 ? "s" : ""}`}
            </button>
          )}
          {showReplies && post.replies?.map((r, i) => (
            <div key={i} className="flex items-start gap-2 mt-2 pl-2 border-l-2 border-[#00E676]/15">
              <Avatar name={r.author_name} avatar={r.author_avatar} />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onMention && r.user_id && onMention(r.user_id)}
                    data-testid={`reply-author-${i}`}
                    className="font-semibold text-xs truncate hover:text-[#39FF14] hover:underline"
                  >
                    {r.author_name}
                  </button>
                  {r.is_champion && <ChampionCrown />}
                  <span className="text-[10px] text-[#697C6F]">{timeAgo(r.created_at)}</span>
                </div>
                <p className="text-xs text-[#C7D3CB] whitespace-pre-wrap break-words">{renderMentions(r.text, members, onMention)}</p>
              </div>
            </div>
          ))}

          {replyOpen && (
            <div className="flex items-end gap-2 mt-2">
              <div className="flex-1">
                <MentionBox value={reply} onChange={setReply} members={members} onSubmit={sendReply}
                  testid={`reply-input-${post.id}`} placeholder="Votre réponse… (tapez @ pour mentionner)" rows={1} />
              </div>
              <button onClick={sendReply} className="p-2 rounded-lg bg-[#00E676] text-[#04180a]"><Send size={14} /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [members, setMembers] = useState([]);
  const [modalUserId, setModalUserId] = useState(null);

  const load = () => api.get("/feed").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  useEffect(() => {
    load();
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const post = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post("/feed", { text, mentions: computeMentions(text, members) });
      setText(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="rounded-2xl border border-[#00E676]/15 bg-[#131F17] flex flex-col h-[520px] md:h-[calc(100vh-8rem)] md:sticky md:top-24" data-testid="feed-panel">
      <div className="p-4 border-b border-[#00E676]/15 flex items-center gap-2">
        <MessageCircle size={18} className="text-[#00E676]" />
        <h2 className="font-bold">Le Mur des turfistes</h2>
      </div>

      <div className="p-3 border-b border-[#00E676]/15">
        <MentionBox value={text} onChange={setText} members={members} onSubmit={post}
          testid="feed-input" placeholder="Partagez une info, un tuyau… (tapez @ pour mentionner un joueur)" rows={2} />
        <div className="flex justify-end mt-2">
          <button onClick={post} disabled={sending} data-testid="feed-post-btn"
            className="text-sm font-bold px-4 py-2 rounded-lg text-[#04180a] bg-gradient-to-r from-[#00E676] to-[#39FF14] disabled:opacity-60 flex items-center gap-2">
            {sending ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Publier
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {posts === null ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#00E676]" /></div>
        ) : posts.length === 0 ? (
          <p className="text-center text-sm text-[#697C6F] py-10">Aucun message pour le moment. Lancez la discussion !</p>
        ) : (
          posts.map((p) => <Post key={p.id} post={p} me={user} onChange={load} members={members} onMention={setModalUserId} />)
        )}
      </div>
      <PlayerModal userId={modalUserId} onClose={() => setModalUserId(null)} />
    </div>
  );
}
